<template>
  <b-modal
    id="uploadFiles"
    ref="uploadFiles"
    size="xl"
    centered
    scrollable
    modal-class="upload-files-modal"
    body-class="p-0"
    @shown="onShown"
    @hidden="setDefaultValue"
  >
    <template #modal-title>
      <span class="uf-title">อัปโหลดไฟล์</span>
      <span v-if="patientDn" class="uf-subtitle">DN {{ patientDn }}</span>
    </template>

    <div class="uf-layout">
      <div
        class="uf-main"
        :class="{ 'is-drag-over': isDragOver }"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <!-- 1. ประเภท -->
        <section class="uf-step">
          <h3 class="uf-step-title"><span class="uf-step-no">1</span>ประเภทไฟล์</h3>
          <div class="uf-categories" role="group" aria-label="ประเภทไฟล์">
            <button
              v-for="cat in categories"
              :key="cat.value"
              type="button"
              class="uf-category"
              :class="{ active: selectedCategory === cat.value }"
              :aria-pressed="selectedCategory === cat.value ? 'true' : 'false'"
              @click="pickCategory(cat.value)"
            >
              <i :class="cat.icon" aria-hidden="true"></i>
              <span class="uf-category-name">{{ cat.text }}</span>
              <span class="uf-category-hint">{{ cat.accept }}</span>
            </button>
          </div>
          <p v-if="attempted && !selectedCategory" class="uf-error">
            เลือกประเภทไฟล์ก่อน
          </p>
        </section>

        <!-- 2. ไฟล์ -->
        <section class="uf-step">
          <div class="uf-step-head">
            <h3 class="uf-step-title"><span class="uf-step-no">2</span>ไฟล์</h3>
            <span v-if="ruleCategory" class="uf-muted">{{ acceptText }}</span>
          </div>

          <div class="uf-dropzone">
            <i class="fas fa-cloud-upload-alt uf-dropzone-icon" aria-hidden="true"></i>
            <div class="uf-dropzone-text">
              <strong>{{ isTouch ? "เพิ่มไฟล์" : "ลากไฟล์มาวาง หรือวางภาพหน้าจอ" }}</strong>
              <span class="uf-muted">เลือกได้หลายไฟล์พร้อมกัน</span>
            </div>
            <div class="uf-sources">
              <b-button
                v-if="canCapture"
                variant="primary"
                class="uf-source"
                @click="$refs.cameraInput.click()"
              >
                <i class="fas fa-camera" aria-hidden="true"></i>
                {{ captureLabel }}
              </b-button>
              <b-button
                v-if="isTouch"
                variant="outline-secondary"
                class="uf-source"
                @click="$refs.libraryInput.click()"
              >
                <i class="far fa-images" aria-hidden="true"></i>
                คลังรูป
              </b-button>
              <b-button
                variant="outline-secondary"
                class="uf-source"
                @click="$refs.fileInput.click()"
              >
                <i class="far fa-file" aria-hidden="true"></i>
                {{ isTouch ? "ไฟล์" : "เลือกไฟล์" }}
              </b-button>
            </div>
            <input
              ref="fileInput"
              type="file"
              multiple
              class="d-none"
              :accept="fileAccept"
              @change="onPicked"
            />
            <input
              ref="libraryInput"
              type="file"
              multiple
              class="d-none"
              accept="image/jpeg,image/png"
              @change="onPicked"
            />
            <input
              ref="cameraInput"
              type="file"
              class="d-none"
              accept="image/jpeg,image/png"
              capture="environment"
              @change="onPicked"
            />
          </div>

          <div v-if="acceptedEntries.length > 0" class="uf-files">
            <div v-for="entry in acceptedEntries" :key="entry.key" class="uf-file">
              <div class="uf-thumb" :class="`kind-${entry.kind}`">
                <span v-if="entry.isImage" class="uf-thumb-square">
                  <img :src="entry.url" :alt="entry.name" :style="{ transform: previewTransform(entry.edit) }" />
                </span>
                <span v-else-if="entry.kind === 'pdf'" class="uf-thumb-label">
                  <i class="far fa-file-pdf" aria-hidden="true"></i> PDF
                </span>
                <span v-else class="uf-thumb-label">DICOM</span>
                <span v-if="entry.pasted" class="uf-badge">วางจากคลิปบอร์ด</span>
                <button
                  type="button"
                  class="uf-remove"
                  :aria-label="`เอา ${entry.name} ออก`"
                  @click="removeEntry(entry)"
                >
                  <i class="fas fa-times" aria-hidden="true"></i>
                </button>
              </div>
              <div v-if="entry.isImage" class="uf-tools">
                <button type="button" aria-label="หมุนซ้าย 90 องศา" title="หมุนซ้าย" @click="editImage(entry, 'rotateLeft')">
                  <i class="fas fa-undo" aria-hidden="true"></i>
                </button>
                <button type="button" aria-label="หมุนขวา 90 องศา" title="หมุนขวา" @click="editImage(entry, 'rotateRight')">
                  <i class="fas fa-redo" aria-hidden="true"></i>
                </button>
                <button type="button" aria-label="พลิกแนวนอน" title="พลิกแนวนอน" @click="editImage(entry, 'flipHorizontal')">
                  <i class="uf-flip uf-flip-h" aria-hidden="true"></i>
                </button>
                <button type="button" aria-label="พลิกแนวตั้ง" title="พลิกแนวตั้ง" @click="editImage(entry, 'flipVertical')">
                  <i class="uf-flip uf-flip-v" aria-hidden="true"></i>
                </button>
              </div>
              <div class="uf-file-meta">
                <span class="uf-file-name" :title="entry.name">{{ entry.name }}</span>
                <span class="uf-muted">{{ formatBytes(entry.size) }}</span>
              </div>
            </div>
          </div>
          <p v-else-if="attempted" class="uf-error">เพิ่มไฟล์อย่างน้อย 1 ไฟล์</p>

          <div v-if="rejectedEntries.length > 0" class="uf-rejected" role="alert">
            <div class="uf-rejected-head">
              <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
              <span>ไม่ได้เพิ่ม {{ rejectedEntries.length }} ไฟล์</span>
              <button type="button" class="uf-link" @click="clearRejected">ล้าง</button>
            </div>
            <div v-for="entry in rejectedEntries" :key="entry.key" class="uf-rejected-row">
              <strong>{{ entry.name }}</strong>
              <span>{{ entry.reason }}</span>
            </div>
          </div>
        </section>

        <div v-if="isDragOver" class="uf-drop-overlay">
          <i class="fas fa-cloud-upload-alt" aria-hidden="true"></i>
          วางไฟล์ที่นี่
        </div>
      </div>

      <!-- 3. รายละเอียด -->
      <aside class="uf-details">
        <h3 class="uf-step-title"><span class="uf-step-no">3</span>รายละเอียด</h3>

        <div v-if="isDocument" class="uf-template">
          <div class="uf-template-text">
            <strong>บันทึกเป็นแบบฟอร์มคลินิก</strong>
            <span class="uf-muted">
              เก็บไว้ใช้กับคนไข้ทุกคน · เฉพาะผู้จัดการสาขา, RDM, C-Level
            </span>
            <button
              v-if="canManageClinicForm && !hasClinicFormPlan"
              type="button"
              class="uf-plan-lock"
              @click="showUpgradeNotice('การเพิ่มแบบฟอร์มคลินิก')"
            >
              <UpgradeBadge />
            </button>
          </div>
          <b-form-checkbox
            v-model="isTemplate"
            v-b-tooltip.hover
            switch
            size="lg"
            :disabled="!canUploadClinicForm"
            :title="templateSwitchTooltip"
            aria-label="บันทึกเป็นแบบฟอร์มคลินิก"
          />
        </div>

        <template v-if="!isTemplate">
          <b-form-group label-for="uf-treatment" class="mb-3">
            <template #label>
              วันที่รักษา <span class="uf-muted">(ไม่บังคับ)</span>
            </template>
            <b-form-select
              id="uf-treatment"
              v-model="selectedTreatmentId"
              :options="treatmentOptions"
            />
            <small class="uf-muted">
              ถ้าไม่ระบุ ชื่อไฟล์ตอนดาวน์โหลดจะใช้วันที่อัปโหลด
            </small>
          </b-form-group>
          <b-form-group label="คำอธิบาย" label-for="uf-remark" class="mb-3">
            <b-form-textarea
              id="uf-remark"
              v-model="remark"
              rows="3"
              no-resize
              placeholder="เช่น ก่อนจัดฟัน, หลังขูดหินปูน"
            />
            <small class="uf-muted">ใช้กับทุกไฟล์ในชุดนี้</small>
          </b-form-group>
          <div v-if="ruleCategory" class="uf-name-preview">
            <span class="uf-name-preview-label">ชื่อไฟล์เมื่อดาวน์โหลด</span>
            <code>{{ namePreview }}</code>
          </div>
        </template>

        <div v-else class="uf-template-names">
          <span class="uf-name-preview-label">ชื่อแบบฟอร์มที่จะบันทึก</span>
          <ul v-if="acceptedEntries.length > 0">
            <li v-for="entry in acceptedEntries" :key="entry.key">
              {{ templateRemarkOf(entry) }}
            </li>
          </ul>
          <small class="uf-muted">
            ใช้ชื่อไฟล์ (ตัดนามสกุลออก) · ไฟล์รูปจะแปลงเป็น PDF 1 หน้าให้อัตโนมัติ
            เพื่อให้เซ็นได้เหมือนแบบฟอร์ม PDF
          </small>
        </div>
      </aside>
    </div>

    <template #modal-footer="{ cancel }">
      <div class="uf-footer">
        <div class="uf-summary">
          <strong>{{ summaryText }}</strong>
          <span v-if="storageWarning" class="uf-warning">{{ storageWarning }}</span>
          <span v-else class="uf-muted">
            เริ่มอัปโหลดแล้วปิดหน้าต่างได้ทันที · ดูความคืบหน้าที่มุมซ้ายล่าง
          </span>
        </div>
        <b-button variant="outline-secondary" @click="cancel()">ยกเลิก</b-button>
        <b-button variant="primary" :disabled="isLoading" @click="saveFiles">
          {{ ctaText }}
        </b-button>
      </div>
    </template>

    <Loading v-if="isLoading"></Loading>
    <Dialog ref="Dialog"></Dialog>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import ClinicFormPermissionMixin from "@/mixins/ClinicFormPermissionMixin";
import UpgradeNoticeMixin from "@/mixins/UpgradeNoticeMixin";
import UpgradeBadge from "@/components/badge/UpgradeBadge.vue";
import bytesToSize from "@/utils/bytesToSize";
import {
  CAMERA_CATEGORIES,
  acceptAttr,
  acceptLabel,
  checkFiles,
  contentTypeOf,
  kindOf,
  needsPdfWrap,
  sizeLabel,
} from "@/utils/uploadRules";
import { imageToPdf } from "@/utils/imageToPdf";
import { buildDownloadName } from "@/utils/downloadNames";
import { isTextField } from "@/utils/pasteTarget";
import { namePasted } from "@/utils/clipboardFiles";
import { NO_EDIT, applyEdit, editTransform } from "@/utils/imageEdit";
import { renderEditedImage } from "@/utils/renderEditedImage";
import { CATEGORY_THEME } from "@/utils/categoryTheme";
import { storageLeft } from "@/utils/storageEntitlement";
import { FEATURE } from "@/constants/entitlements";

const CATEGORIES = [
  "IntraOralPhoto",
  "ExtraOralPhoto",
  "XRay",
  "OpdCard",
  "Document",
].map(value => ({
  value,
  text: CATEGORY_THEME[value].label,
  icon: CATEGORY_THEME[value].icon,
}));

const CAPTURE_LABELS = {
  IntraOralPhoto: "ถ่ายรูป",
  ExtraOralPhoto: "ถ่ายรูป",
  OpdCard: "ถ่ายบัตร",
  Document: "ถ่ายเอกสาร",
};

let entryKey = 0;

export default {
  name: "uploadFiles",
  mixins: [ClinicFormPermissionMixin, UpgradeNoticeMixin],
  components: {
    Dialog,
    Loading,
    UpgradeBadge,
  },
  props: ["patientId", "patientDn", "treatmentId", "labId"],
  data() {
    return {
      selectedCategory: null,
      isTemplate: false,
      selectedTreatmentId: null,
      remark: "",
      // { key, file, name, kind, isImage, size, url, edit, pasted }
      // edit: หมุน/พลิกที่สั่งไว้ (imageEdit) วาดจริงครั้งเดียวตอนอัปโหลด
      entries: [],
      attempted: false,
      // อ่านจากแกลเลอรี: ระหว่างเปิดอยู่ Ctrl+V เป็นของหน้านี้
      isOpen: false,
      isLoading: false,
      isDragOver: false,
      dragDepth: 0,
      isTouch:
        typeof window !== "undefined" &&
        !!window.matchMedia &&
        window.matchMedia("(pointer: coarse)").matches,
    };
  },
  computed: {
    ...mapGetters({
      getTreatmentHistoryList: "moduleTreatment/getTreatmentHistoryList",
      getBranchInfo: "moduleUser/getBranchInfo",
      entitlementLimit: "moduleBranch/entitlementLimit",
      entitlementUsed: "moduleBranch/entitlementUsed",
    }),
    categories() {
      return CATEGORIES.map(cat => ({ ...cat, accept: acceptLabel(cat.value) }));
    },
    treatmentList() {
      return this.$utils
        .removeDuplicates(this.getTreatmentHistoryList || [], "id")
        .map(i => ({
          id: i.id,
          dateTime: this.$utils.formatDateTime(i.creationDt),
          creationDt: i.creationDt,
        }))
        .sort((a, b) => new Date(b.creationDt) - new Date(a.creationDt));
    },
    treatmentOptions() {
      return [
        { value: null, text: "ไม่ระบุวันที่รักษา" },
        ...this.treatmentList.map(t => ({ value: t.id, text: t.dateTime })),
      ];
    },
    isDocument() {
      return this.selectedCategory === "Document";
    },
    // หมวดที่ใช้ตรวจชนิด/ขนาดไฟล์ — แบบฟอร์มคลินิกมีกติกาของตัวเอง
    ruleCategory() {
      return this.isTemplate ? "Template" : this.selectedCategory;
    },
    fileAccept() {
      if (this.ruleCategory) return acceptAttr(this.ruleCategory);
      // ยังไม่เลือกประเภท: เปิดให้เลือกได้ทุกชนิดที่หมวดใดหมวดหนึ่งรับ
      return [
        ...new Set(["XRay", "Document"].flatMap(c => acceptAttr(c).split(","))),
      ].join(",");
    },
    acceptText() {
      return `${acceptLabel(this.ruleCategory)} · ${sizeLabel(this.ruleCategory)}`;
    },
    canCapture() {
      return this.isTouch && CAMERA_CATEGORIES.includes(this.selectedCategory);
    },
    captureLabel() {
      return CAPTURE_LABELS[this.selectedCategory] || "ถ่ายรูป";
    },
    // ตรวจกับประเภทที่เลือกอยู่ทุกครั้ง: เปลี่ยนประเภทแล้วไฟล์ที่ใช้ได้กลับมาเอง
    checkedEntries() {
      if (!this.ruleCategory) {
        return this.entries.map(entry => ({ entry, reason: null }));
      }
      return this.entries.map(entry => {
        const { rejected } = checkFiles([entry.file], this.ruleCategory);
        return { entry, reason: rejected.length ? rejected[0].reason : null };
      });
    },
    acceptedEntries() {
      return this.checkedEntries.filter(c => !c.reason).map(c => c.entry);
    },
    rejectedEntries() {
      return this.checkedEntries
        .filter(c => c.reason)
        .map(c => ({ key: c.entry.key, name: c.entry.name, reason: c.reason }));
    },
    totalBytes() {
      return this.acceptedEntries.reduce((sum, e) => sum + (e.size || 0), 0);
    },
    // โควตาจาก entitlements ไม่ใช่ branch.fileStoreQuota ที่ BE เลิกเขียนแล้ว —
    // null = ไม่แสดงตัวเลข (PREMIUM ไม่จำกัด)
    storageLeft() {
      const entitledUsed = this.entitlementUsed(FEATURE.STORAGE);
      const used =
        entitledUsed !== null ? entitledUsed : Number(this.getBranchInfo?.fileStoreUsage) || 0;
      return storageLeft(this.entitlementLimit(FEATURE.STORAGE), used);
    },
    summaryText() {
      const count = this.acceptedEntries.length;
      if (count === 0) return "ยังไม่ได้เลือกไฟล์";
      const unit = this.isTemplate ? "แบบฟอร์ม" : "ไฟล์";
      const parts = [`${count} ${unit}`, this.formatBytes(this.totalBytes)];
      if (this.storageLeft !== null) {
        parts.push(`พื้นที่สาขาเหลือ ${this.formatBytes(this.storageLeft)}`);
      }
      return parts.join(" · ");
    },
    // ตัวเลขพื้นที่โหลดมาตอนเข้าสู่ระบบ อาจไม่ล่าสุด จึงเตือนเฉยๆ ให้เซิร์ฟเวอร์ตัดสิน
    storageWarning() {
      if (this.storageLeft === null || this.totalBytes <= this.storageLeft) {
        return "";
      }
      return "พื้นที่สาขาอาจไม่พอสำหรับไฟล์ชุดนี้";
    },
    ctaText() {
      const count = this.acceptedEntries.length;
      if (this.isTemplate) return count ? `บันทึก ${count} แบบฟอร์ม` : "บันทึกแบบฟอร์ม";
      return count ? `อัปโหลด ${count} ไฟล์` : "อัปโหลด";
    },
    namePreview() {
      const first = this.acceptedEntries[0];
      const treatment = this.treatmentList.find(t => t.id === this.selectedTreatmentId);
      return buildDownloadName(
        {
          category: this.ruleCategory,
          remark: this.remark,
          fileType: first ? contentTypeOf(first.file) : "image/jpeg",
          creationDt: new Date(),
        },
        { dn: this.patientDn, treatmentDate: treatment && treatment.creationDt }
      );
    },
    templateSwitchTooltip() {
      return this.clinicFormBlockedReason || "เก็บไว้ใช้กับคนไข้ทุกท่าน (ใช้ได้กับประเภทเอกสารเท่านั้น)";
    },
    // ทำไมอัปโหลดเป็นแบบฟอร์มคลินิกไม่ได้ — ว่างเมื่อทำได้
    clinicFormBlockedReason() {
      if (!this.canManageClinicForm) {
        return "เพิ่มแบบฟอร์มคลินิกได้เฉพาะผู้จัดการสาขา หรือผู้บริหารระดับคลินิก (RDM / C-Level)";
      }
      if (!this.hasClinicFormPlan) {
        return "การเพิ่มแบบฟอร์มคลินิกไม่รวมอยู่ในแพ็กปัจจุบันของสาขา";
      }
      return "";
    },
  },
  methods: {
    ...mapActions({
      createMediaFiles: "moduleMediaFile/createMediaFiles",
    }),
    // `files`: already picked elsewhere, e.g. a photo just taken on a phone.
    show({ category, treatmentId, files } = {}) {
      this.$refs.uploadFiles.show();
      this.selectedCategory = category || null;
      if (category === "Template") {
        this.selectedCategory = "Document";
        this.isTemplate = this.canUploadClinicForm;
      }
      this.selectedTreatmentId = treatmentId || null;
      this.attempted = false;
      if (files && files.length) this.addFiles(files);
    },
    close() {
      this.$refs.uploadFiles.hide();
    },
    onShown() {
      this.isOpen = true;
      document.addEventListener("paste", this.onPaste);
    },
    pickCategory(value) {
      this.selectedCategory = value;
      if (value !== "Document") this.isTemplate = false;
    },
    formatBytes(bytes) {
      return bytes ? bytesToSize(bytes) : "0 Bytes";
    },

    // ---- เพิ่มไฟล์: เลือก / ลากวาง / วางจากคลิปบอร์ด / กล้อง ----
    onPicked(event) {
      this.addFiles(Array.from(event.target.files || []));
      // เลือกไฟล์เดิมซ้ำได้อีก
      event.target.value = "";
    },
    onDragEnter() {
      this.dragDepth += 1;
      this.isDragOver = true;
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.isDragOver = false;
    },
    onDrop(event) {
      this.dragDepth = 0;
      this.isDragOver = false;
      this.addFiles(Array.from(event.dataTransfer.files || []));
    },
    onPaste(event) {
      // พิมพ์ในช่องข้อความอยู่ ปล่อยให้วางข้อความตามปกติ
      if (isTextField(event.target)) return;
      const files = Array.from(
        (event.clipboardData && event.clipboardData.files) || []
      );
      if (files.length === 0) return;
      event.preventDefault();
      this.addFiles(files.map((file, index) => namePasted(file, index)), { pasted: true });
    },

    addFiles(files, { pasted = false } = {}) {
      files.forEach(file => {
        const kind = kindOf(file);
        const isImage = kind === "jpg" || kind === "png";
        const entry = {
          key: `f${(entryKey += 1)}`,
          file,
          name: file.name || "ไฟล์",
          kind,
          isImage,
          size: file.size,
          url: isImage ? URL.createObjectURL(file) : null,
          edit: NO_EDIT,
          pasted,
        };
        this.entries.push(entry);
      });
    },
    removeEntry(entry) {
      this.revoke(entry);
      this.entries = this.entries.filter(e => e.key !== entry.key);
    },
    clearRejected() {
      const rejected = new Set(this.rejectedEntries.map(r => r.key));
      this.entries.filter(e => rejected.has(e.key)).forEach(this.revoke);
      this.entries = this.entries.filter(e => !rejected.has(e.key));
    },
    revoke(entry) {
      if (entry.url && entry.url.startsWith("blob:")) URL.revokeObjectURL(entry.url);
    },

    // ---- หมุน/พลิก: กดแล้วแค่จำไว้และหมุนภาพตัวอย่างด้วย CSS ----
    // วาดพิกเซลจริงครั้งเดียวตอนกดอัปโหลด (renderEditedImage) กดกี่ครั้งก็ไม่หน่วง
    // และกดจนกลับท่าเดิม (หมุนครบรอบ/พลิกสองครั้ง) ไฟล์เดิมถูกส่งไปโดยไม่เข้ารหัสใหม่
    editImage(entry, action) {
      entry.edit = applyEdit(entry.edit, action);
    },
    previewTransform(edit) {
      return editTransform(edit);
    },

    // ---- บันทึก ----
    templateRemarkOf(entry) {
      return this.$utils.stripFileExtension(entry.name) || entry.name;
    },
    async saveFiles() {
      this.attempted = true;
      if (!this.ruleCategory || this.acceptedEntries.length === 0) return;
      if (this.isTemplate && !this.canUploadClinicForm) {
        this.$refs.Dialog.showAlert("อัปโหลดแบบฟอร์มไม่ได้", this.clinicFormBlockedReason);
        return;
      }

      // พอทุกไฟล์ได้ presigned URL แปลว่าเซิร์ฟเวอร์รับชุดนี้แล้ว (โควตา/validation ผ่าน)
      // ที่เหลือคือการส่งไฟล์ ซึ่งแถบอัปโหลดมุมซ้ายล่างแสดงความคืบหน้าและปุ่มลองใหม่ให้
      // จึงปิด modal ตรงนี้เลย ผู้ใช้ทำงานอื่นต่อได้ระหว่างรอ
      let handedOff = false;
      const handOff = () => {
        if (handedOff) return;
        handedOff = true;
        this.isLoading = false;
        this.close();
      };
      const isTemplate = this.isTemplate;
      const accepted = [...this.acceptedEntries];
      const names = accepted.map(e => e.name);

      this.isLoading = true;
      try {
        // ทีละไฟล์: รูปเต็มขนาดที่ถอดรหัสแล้วกินหน่วยความจำหลายสิบ MB ต่อรูป
        const files = [];
        for (const entry of accepted) {
          const file = entry.isImage
            ? await renderEditedImage(entry.file, entry.edit)
            : entry.file;
          // แบบฟอร์มคลินิกต้องเป็น PDF (หน้าเซ็นเปิดด้วย PDF reader) รูปจึงถูกแปลงเป็น PDF หน้าเดียว
          files.push(
            isTemplate && needsPdfWrap(entry.file, "Template")
              ? await imageToPdf(file, entry.name)
              : file
          );
        }
        const filesMetadata = files.map((file, i) => ({
          contentType: file.type || contentTypeOf(accepted[i].file),
          fileSize: file.size,
        }));
        const { completed } = await this.createMediaFiles({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          data: isTemplate
            ? {
                category: "Template",
                takenAt: null,
                patientId: null,
                treatmentId: null,
                labId: null,
                filesMetadata,
              }
            : {
                category: this.selectedCategory,
                takenAt: null,
                patientId: this.patientId ? String(this.patientId) : null,
                treatmentId: this.selectedTreatmentId
                  ? String(this.selectedTreatmentId)
                  : null,
                labId: this.labId ? String(this.labId) : null,
                remark: this.remark,
                filesMetadata,
              },
          files,
          names,
          // แบบฟอร์มแต่ละไฟล์ใช้ชื่อไฟล์ของตัวเองเป็นชื่อแบบฟอร์ม
          remarks: isTemplate ? accepted.map(this.templateRemarkOf) : undefined,
          onPresigned: handOff,
        });
        // ส่งไฟล์ที่ลงทะเบียนแล้ว (มี id และ URL) ให้แกลเลอรีแสดงได้ทันที ไม่ต้อง poll
        this.$emit(isTemplate ? "uploadedTemplateFiles" : "uploadedFiles", completed);
      } catch (err) {
        console.error(err);
        // หลัง hand-off แล้ว แถบอัปโหลดแจ้งไฟล์ที่ไม่สำเร็จพร้อมปุ่มลองใหม่อยู่แล้ว
        if (!handedOff && !this.errorShownElsewhere(err)) {
          this.$refs.Dialog.showAlert("อัปโหลดไม่สำเร็จ", "กรุณาลองใหม่อีกครั้ง");
        }
      } finally {
        if (!handedOff) this.isLoading = false;
      }
    },
    // ข้อผิดพลาดตอน presign ที่มีคนแจ้งผู้ใช้แล้ว: โควตาเต็ม/แพ็กหมดอายุ (alertSwal ใน store)
    // และ error จาก API ที่ global handler แสดงให้ — ยกเว้น 403 ที่ presign ขอข้ามไว้
    errorShownElsewhere(err) {
      const msg = err.response?.data?.errorMessage;
      if (msg === "file limit exceeded" || msg === "file store plan expired.") {
        return true;
      }
      return !!err.isAxiosError && err.response?.status !== 403;
    },
    setDefaultValue() {
      this.isOpen = false;
      document.removeEventListener("paste", this.onPaste);
      this.entries.forEach(this.revoke);
      this.entries = [];
      this.selectedCategory = null;
      this.selectedTreatmentId = null;
      this.remark = "";
      this.isTemplate = false;
      this.attempted = false;
      this.isDragOver = false;
      this.dragDepth = 0;
    },
  },
  beforeDestroy() {
    document.removeEventListener("paste", this.onPaste);
    this.entries.forEach(this.revoke);
  },
};
</script>

<style>
/* มือถือ: เปิดเป็นแผ่นเลื่อนขึ้นจากด้านล่าง เต็มความกว้าง */
@media (max-width: 575.98px) {
  .upload-files-modal .modal-dialog {
    margin: 0;
    max-width: 100%;
    min-height: 100%;
    align-items: flex-end;
  }
  .upload-files-modal .modal-dialog-scrollable {
    max-height: 100%;
    height: 100%;
  }
  .upload-files-modal .modal-content {
    border: 0;
    border-radius: 16px 16px 0 0;
    max-height: calc(100% - 24px);
  }
}
</style>

<style scoped>
.uf-title {
  font-weight: 600;
}
.uf-subtitle {
  margin-left: 0.5rem;
  font-size: 0.875rem;
  font-weight: 400;
  color: #5b6472;
}
.uf-muted {
  color: #5b6472;
  font-size: 0.8125rem;
}
.uf-error {
  margin: 0.25rem 0 0;
  color: #b42318;
  font-size: 0.875rem;
}
.uf-warning {
  color: #8f1d14;
  font-size: 0.8125rem;
  font-weight: 600;
}
.uf-link {
  border: 0;
  background: transparent;
  padding: 0;
  color: var(--primary, #0b63c5);
  font-size: 0.8125rem;
}

.uf-layout {
  display: flex;
  min-height: 0;
}
.uf-main {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.uf-details {
  flex: 0 0 320px;
  padding: 1.25rem 1.5rem;
  background: #f8f9fb;
  border-left: 1px solid #e3e6eb;
}

.uf-step {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.uf-step-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.uf-step-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
}
.uf-step-no {
  width: 22px;
  height: 22px;
  border-radius: 11px;
  background: #14181f;
  color: #fff;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.uf-categories {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.625rem;
}
.uf-category {
  min-height: 88px;
  padding: 0.625rem 0.75rem;
  border: 1px solid #d5dbe3;
  border-radius: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0.125rem;
  text-align: left;
}
.uf-category:hover {
  border-color: var(--primary, #0b63c5);
}
.uf-category i {
  font-size: 1.25rem;
  color: #5b6472;
  margin-bottom: 0.25rem;
}
.uf-category.active {
  border: 2px solid var(--primary, #0b63c5);
  background: #e8f1fc;
}
.uf-category.active i {
  color: var(--primary, #0b63c5);
}
.uf-category-name {
  font-weight: 600;
  color: #14181f;
}
.uf-category-hint {
  font-size: 0.75rem;
  color: #5b6472;
}

.uf-dropzone {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: 1.5px dashed #b8c0cc;
  border-radius: 12px;
  background: #f7f9fc;
}
.uf-dropzone-icon {
  font-size: 1.5rem;
  color: var(--primary, #0b63c5);
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #e8f1fc;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.uf-dropzone-text {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}
.uf-sources {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.uf-source {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 10px;
}

.uf-files {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}
.uf-file {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}
.uf-thumb {
  position: relative;
  height: 112px;
  border: 1px solid #e3e6eb;
  border-radius: 10px 10px 0 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f3f6;
}
/* กรอบจัตุรัส: หมุน 90 องศาแล้วภาพยังอยู่ในกรอบ */
.uf-thumb-square {
  height: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.uf-thumb-square img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.15s ease;
}
.uf-thumb.kind-pdf {
  background: #fbf1f0;
  color: #b42318;
  border-radius: 10px;
}
.uf-thumb.kind-dicom {
  background: #1f242c;
  color: #c9d0da;
  border-radius: 10px;
}
.uf-thumb-label {
  font-weight: 700;
  letter-spacing: 0.06em;
  font-size: 0.8125rem;
}
.uf-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 0 0.5rem;
  border-radius: 6px;
  background: #14181f;
  color: #fff;
  font-size: 0.6875rem;
}
.uf-remove {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 14px;
  background: rgba(20, 24, 31, 0.72);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.uf-tools {
  margin-top: -0.375rem;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  border: 1px solid #e3e6eb;
  border-top: 0;
  border-radius: 0 0 10px 10px;
  background: #fff;
}
.uf-tools button {
  width: 32px;
  height: 30px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #3b4350;
}
.uf-tools button:hover {
  background: #eef1f5;
}
.uf-flip {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  vertical-align: middle;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}
.uf-flip-h {
  background-image: url("~@/assets/icon/reflect-horizontal.svg");
}
.uf-flip-v {
  background-image: url("~@/assets/icon/reflect-vertical.svg");
}
.uf-file-meta {
  display: flex;
  gap: 0.375rem;
  font-size: 0.75rem;
  min-width: 0;
}
.uf-file-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uf-rejected {
  border: 1px solid #f1c9c4;
  background: #fdf3f2;
  border-radius: 10px;
  padding: 0.625rem 0.875rem;
  font-size: 0.8125rem;
}
.uf-rejected-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #8f1d14;
  font-weight: 600;
}
.uf-rejected-head span {
  flex: 1 1 auto;
}
.uf-rejected-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.25rem 0 0 1.5rem;
  color: #6b2a24;
}
.uf-rejected-row strong {
  color: #14181f;
  font-weight: 500;
}

.uf-drop-overlay {
  position: absolute;
  inset: 0.5rem;
  border: 2px dashed var(--primary, #0b63c5);
  border-radius: 12px;
  background: rgba(232, 241, 252, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--primary, #0b63c5);
  pointer-events: none;
}

.uf-template {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.875rem;
  margin-bottom: 1rem;
  background: #fff;
  border: 1px solid #e3e6eb;
  border-radius: 12px;
}
.uf-template-text {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.uf-plan-lock {
  align-self: flex-start;
  margin-top: 0.25rem;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}
.uf-name-preview {
  padding-top: 0.75rem;
  border-top: 1px solid #e3e6eb;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.uf-name-preview code {
  display: block;
  padding: 0.5rem 0.625rem;
  background: #fff;
  border: 1px solid #e3e6eb;
  border-radius: 8px;
  color: #14181f;
  font-size: 0.75rem;
  word-break: break-all;
}
.uf-name-preview-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #3b4350;
}
.uf-template-names ul {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0;
  background: #fff;
  border: 1px solid #e3e6eb;
  border-radius: 10px;
}
.uf-template-names li {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #eef0f3;
  word-break: break-word;
}
.uf-template-names li:last-child {
  border-bottom: 0;
}

.uf-footer {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.uf-summary {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.uf-footer .btn {
  min-height: 44px;
  border-radius: 10px;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

@media (max-width: 991.98px) {
  .uf-layout {
    flex-direction: column;
  }
  .uf-details {
    flex-basis: auto;
    border-left: 0;
    border-top: 1px solid #e3e6eb;
  }
  .uf-categories {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 575.98px) {
  .uf-main,
  .uf-details {
    padding: 1rem;
  }
  .uf-category {
    min-height: 48px;
    align-items: center;
    text-align: center;
  }
  .uf-category i,
  .uf-category-hint {
    display: none;
  }
  .uf-dropzone {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
  .uf-dropzone-icon {
    display: none;
  }
  .uf-sources {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  }
  .uf-source {
    min-height: 72px;
    flex-direction: column;
    justify-content: center;
    gap: 0.25rem;
  }
  .uf-files {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .uf-thumb {
    height: 88px;
  }
  .uf-tools {
    gap: 0;
  }
  .uf-tools button {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
    height: 36px;
  }
  .uf-footer {
    flex-wrap: wrap;
  }
  .uf-summary {
    flex-basis: 100%;
  }
  .uf-footer .btn-primary {
    flex: 1 1 auto;
  }
  /* iOS ซูมหน้าเมื่อช่องกรอกตัวอักษรเล็กกว่า 16px */
  .uf-details select,
  .uf-details textarea {
    font-size: 16px;
  }
}
</style>
